import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SegmentedControl, type SegmentedOption } from "@nexus/ui";
import {
  eventTimeRangeLabel,
  formatEventWindowShort,
  EVENT_TIME_RANGES,
  type EventTimeRange,
} from "../constants/event-time-ranges";

/**
 * Five mutually exclusive windows that an operator flips between constantly
 * while triaging, so they are all on screen rather than behind a menu — the
 * width a dropdown saves costs a click and hides that 30 days is even offered.
 *
 * It sits at the head of the KPI strip because that is where the window was
 * already being announced, and because everything below it — the strip's own
 * totals, the chart, the table — is scoped by it. In the filter bar it would
 * have read as one more column filter.
 */
export function EventsTimeRangeControl({
  value,
  onChange,
}: {
  value: EventTimeRange;
  onChange: (value: EventTimeRange) => void;
}) {
  const { t } = useTranslation();

  const options = useMemo<SegmentedOption[]>(
    () =>
      EVENT_TIME_RANGES.map(({ value: range, hours }) => ({
        value: range,
        label: formatEventWindowShort(hours, t),
        // The label is an abbreviation, so the full range is what a hover and a
        // screen reader get.
        title: eventTimeRangeLabel(range, t),
      })),
    [t],
  );

  return (
    <SegmentedControl
      value={value}
      onValueChange={(next) => onChange(next as EventTimeRange)}
      options={options}
      ariaLabel={t("admin.events.filters.timeRangeLabel", "Time range")}
      className="tabular-nums"
    />
  );
}
